import React, { useState, useEffect } from 'react';
import { queryFamilyForDonation } from "../../service/DonationApi";
import { queryFamiliesByArea } from "../../service/FamilyQueryApi";
import { getUsedAddressArea } from "../../service/AddressApi";
import { useCheckAccess, getSubareaNameTamilWithoutArea, formatINR } from "../../utils/Utilities";
import { Loading, Breadcrumbs } from "../../components/shared";
import { useNavigate } from 'react-router-dom';
import { statusExplanation } from '../general/DropDownValuesConfig';

const FamilySearch = () => {
  useCheckAccess('FamilyRO');

  // 🔹 Direct
  const [familyId, setFamilyId] = useState('');
  const [mobile, setMobile] = useState('');
  const [aadhar, setAadhar] = useState('');

  // 🔹 Address
  const [district, setDistrict] = useState('');
  const [block, setBlock] = useState('');
  const [area, setArea] = useState('');
  const [pincode, setPincode] = useState('');
  const [subarea, setSubarea] = useState('');

  const [areasMaster, setAreasMaster] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);

  const [searchMode, setSearchMode] = useState(null);

  const [searchResult, setSearchResult] = useState(null);
  const [areaFamilies, setAreaFamilies] = useState([]);
  const [tempAreaFamilies, setTempAreaFamilies] = useState([]);


  const [loading, setLoading] = useState(false);

  const [tempAddressTamil, setTempAddressTamil] = useState('');
  const [permanentAddressTamil, setPermanentAddressTamil] = useState('');
  const [searchError, setSearchError] = useState('');
  const [addressError, setAddressError] = useState('');
  const navigate = useNavigate();
  const hasDirectInput = familyId || mobile || aadhar;
  const isDirectDisabled = searchMode === 'address';
  //const isAddressDisabled = searchMode === 'direct';

  // 🔹 Load master
  useEffect(() => {
    const load = async () => {
      const res = await getUsedAddressArea();
      setAreasMaster(res.addressAreaList || []);
    };
    load();
  }, []);

  // 🔍 DIRECT SEARCH
  const handleDirectSearch = async (idOverride) => {
    setSearchMode('direct');

    const idToSearch = idOverride || familyId;

    setLoading(true);
    setAreaFamilies([]);
    setTempAreaFamilies([]);
    setAddressSuggestions([]);
    setSearchResult(null);
    setSearchError('');
    setAddressError('');


    try {
      const response = await queryFamilyForDonation({
        familyId: idToSearch,
        mobile,
        aadhar
      });

      if (response?.family) {
        setSearchResult(response);

        const {
          tempAddressAreaId,
          tempSubareaId,
          permanentAddressAreaId,
          permanentSubareaId
        } = response.family;

        if (tempAddressAreaId && tempSubareaId) {
          const name = await getSubareaNameTamilWithoutArea(tempAddressAreaId, tempSubareaId);
          setTempAddressTamil(name);
        }

        if (permanentAddressAreaId && permanentSubareaId) {
          const name = await getSubareaNameTamilWithoutArea(permanentAddressAreaId, permanentSubareaId);
          setPermanentAddressTamil(name);
        }
      } else {
        setSearchError("❌ குடும்பம் கிடைக்கவில்லை");
        setSearchMode(null);
      }
    } catch (e) {
      setSearchError("❌ தேடலில் பிழை ஏற்பட்டது");
    } finally {
      setLoading(false);
    }
  };

  // 🔍 ADDRESS TYPING (FIXED FULL LOGIC)
  const handleAddressTyping = (value, field) => {
    setSearchMode('address');
    let newSubarea = subarea;
    let newDistrict = district;
    let newBlock = block;
    let newArea = area;
    let newPincode = pincode;

    if (field === 'district') newDistrict = value;
    if (field === 'block') newBlock = value;
    if (field === 'area') newArea = value;
    if (field === 'pincode') newPincode = value;

    if (field === 'subarea') newSubarea = value;

    setSubarea(newSubarea);
    setDistrict(newDistrict);
    setBlock(newBlock);
    setArea(newArea);
    setPincode(newPincode);

    setSearchResult(null);
    setAreaFamilies([]);
    setTempAreaFamilies([]);
    setSearchError('');
    setAddressError('');

    // 🔥 if all empty → clear
    if (!newDistrict && !newBlock && !newArea && !newSubarea && !newPincode) {
      setAddressSuggestions([]);
      setSearchMode(null);
      setAddressError('');
      return;
    }

    const filtered = areasMaster.filter(a => {

      const subAreaMatch = a.subAreas?.some(sa =>
      (!newSubarea || (`${sa.name} ${sa.name_tamil}`)
        .toLowerCase()
        .includes(newSubarea.toLowerCase()))
      );

      return (
        (!newDistrict || `${a.district} ${a.district_tamil}`.toLowerCase().includes(newDistrict.toLowerCase())) &&
        (!newBlock || `${a.block} ${a.block_tamil}`.toLowerCase().includes(newBlock.toLowerCase())) &&
        (!newArea || `${a.name} ${a.name_tamil}`.toLowerCase().includes(newArea.toLowerCase())) &&
        (!newSubarea || subAreaMatch) &&
        (!newPincode || a.pincode.includes(newPincode))
      );
    });

    if (filtered.length === 0) {
      setAddressSuggestions([]);
      setAddressError("❌ முகவரி கிடைக்கவில்லை");
    } else {
      setAddressSuggestions(filtered.slice(0, 10));
    }
  };

  // ✅ SELECT ADDRESS
  const handleSelectAddress = async (a) => {
    setAddressSuggestions([]);
    setSearchResult(null);
    setAddressError('');

    setLoading(true);

    try {
      const response = await queryFamiliesByArea({
        district: a.district_tamil,
        block: a.block_tamil,
        areaId: a.id,
        subareaId: a.subArea?.id, // 🔥 NEW
        pageNumber: 1,
        pageSize: 20
      });

      if (!response?.families?.length) {
        setAddressError("❌ இந்த முகவரிக்கு குடும்பம் இல்லை");
        setAreaFamilies([]);
      } else {
        setAreaFamilies(response.families);
      }
      if (!response?.tempFamilies?.length) {
        //setAddressError("❌ இந்த முகவரிக்கு குடும்பம் இல்லை");
        setTempAreaFamilies([]);
      } else {
        setTempAreaFamilies(response.tempFamilies);
      }
    } catch (e) {
      setAddressError("❌ முகவரி தேடலில் பிழை");
    } finally {
      setLoading(false);
    }
  };
  const handleDirectInputChange = (value, field) => {
    let newFamilyId = familyId;
    let newMobile = mobile;
    let newAadhar = aadhar;

    if (field === 'familyId') newFamilyId = value;
    if (field === 'mobile') newMobile = value;
    if (field === 'aadhar') newAadhar = value;

    setFamilyId(newFamilyId);
    setMobile(newMobile);
    setAadhar(newAadhar);

    // 🔥 If all empty → unlock address
    if (!newFamilyId && !newMobile && !newAadhar) {
      setSearchMode(null);
    } else {
      setSearchMode('direct');
    }

    // optional: clear old results when editing
    setSearchResult(null);
    setSearchError('');
  };
  // 🔥 LIST → DIRECT SEARCH
  const handleSelectFamily = (id) => {
    setFamilyId(id);
    handleDirectSearch(id);
  };

  return (
    <div className="general_parent">
      <div className="general_container">
        <div className="general_body">

          <Breadcrumbs /><br />
          <h2>குடும்பம் தேடல்</h2>

          {/* 🔢 DIRECT */}
          <form
            className="auth_element"
            onSubmit={(e) => {
              e.preventDefault();
              handleDirectSearch();
            }}
          >
            <label className="auth_label">குடும்ப எண்:</label>
            <input disabled={isDirectDisabled} value={familyId}
              onChange={e => handleDirectInputChange(e.target.value, 'familyId')}
              className="auth_input"
              inputMode="numeric"
              maxLength="4" />

            <label className="auth_label">மொபைல்:</label>
            <input disabled={isDirectDisabled} value={mobile}
              onChange={e => handleDirectInputChange(e.target.value, 'mobile')}
              className="auth_input"
              inputMode="numeric"
              maxLength="10"
            />

            <label className="auth_label">ஆதார்:</label>
            <input disabled={isDirectDisabled} value={aadhar}
              onChange={e => handleDirectInputChange(e.target.value, 'aadhar')}
              className="auth_input"
              inputMode="numeric"
              maxLength="20"
            />

            <button onClick={() => handleDirectSearch()}>
              குடும்பத்தை தேடு
            </button>
            {searchError && <p style={{ color: 'red' }}>{searchError}</p>}
          </form>

          {/* 📍 ADDRESS */}
          {!hasDirectInput && (
            <div className="auth_element" style={{ marginTop: '1rem' }}>
              <h3>முகவரி மூலம் தேடல்</h3>

              <input value={district}
                onChange={e => handleAddressTyping(e.target.value, 'district')}
                placeholder="மாவட்டம்"
                className="auth_input" />

              <input value={block}
                onChange={e => handleAddressTyping(e.target.value, 'block')}
                placeholder="தாலுகா"
                className="auth_input" />

              <input value={area}
                onChange={e => handleAddressTyping(e.target.value, 'area')}
                placeholder="தபால்"
                className="auth_input" />

              <input
                value={subarea}
                onChange={e => handleAddressTyping(e.target.value, 'subarea')}
                placeholder="ஊர்"
                className="auth_input"
              />

              <input value={pincode}
                onChange={e => handleAddressTyping(e.target.value, 'pincode')}
                placeholder="Pincode"
                className="auth_input" />
            </div>
          )}

          {/* 🔽 Suggestions */}
          {addressSuggestions.length > 0 && (
            <div className="details-card">
              {addressSuggestions.flatMap(a => {
                const filteredSubareas = a.subAreas?.filter(sa =>
                  !subarea ||
                  (`${sa.name} ${sa.name_tamil}`)
                    .toLowerCase()
                    .includes(subarea.toLowerCase())
                );

                return (filteredSubareas?.length > 0
                  ? filteredSubareas.map(sa => ({ ...a, subArea: sa }))
                  : (!subarea ? [{ ...a, subArea: null }] : [])
                );
              }).map(item => (
                <div
                  key={`${item.id}_${item.subArea?.id || 'noSub'}`}
                  style={{ cursor: 'pointer', padding: '6px' }}
                  onClick={() => handleSelectAddress(item)}
                >
                  <strong>
                    {item.subArea?.name_tamil?.split('|').reverse().join(',') || item.name_tamil}
                  </strong>
                  <br />
                  {item.name_tamil}, {item.block_tamil}, {item.district_tamil}
                  <hr />
                </div>
              ))}
            </div>
          )}
          {addressError && <p style={{ color: 'red' }}>{addressError}</p>}
          {loading && <Loading />}

          {/* 📍 LIST */}
          {!hasDirectInput && areaFamilies.length > 0 && (
            <div className="details-card">
              <h4>குடும்பங்கள்</h4>

              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>எண்</th>
                      <th>விவரம்</th>
                      <th>வரி</th>
                      <th>திருப்பணி</th>
                    </tr>
                  </thead>

                  <tbody>
                    {areaFamilies.map(f => (
                      <tr
                        key={f.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectFamily(f.id)}
                        title="Click to select family"
                      >
                        <td>{f.id}</td>

                        <td>
                          <strong>
                            {f.familyHeadFirstName} {f.familyHeadLastName}{f.familyName && (' (' + f.familyName + ')')}
                          </strong>
                          <br />
                          📱 {f.familyHeadMobile}
                          {f.familyHeadMobile2 ? ` / ${f.familyHeadMobile2}` : ''}
                          <br /> {f.familyOrigin && ('பூர்வீகம்: ' + f.familyOrigin)}
                        </td>

                        <td>
                          {f.pongalTaxUptoYear || ''}
                        </td>

                        <td>
                          {f.chinnaKovil2025TempleConstructionTotal
                            ? formatINR(f.chinnaKovil2025TempleConstructionTotal)
                            : ''
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {
            <br></br>
          }
          {!hasDirectInput && tempAreaFamilies.length > 0 && (
            <div className="details-card">
              <h4>தற்காலிக முகவரி குடும்பங்கள்</h4>

              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>எண்</th>
                      <th>விவரம்</th>
                      <th>வரி</th>
                      <th>திருப்பணி</th>
                    </tr>
                  </thead>

                  <tbody>
                    {tempAreaFamilies.map(f => (
                      <tr
                        key={f.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectFamily(f.id)}
                        title="Click to select family"
                      >
                        <td><strong>{f.id}</strong></td>

                        <td>
                          <strong>
                            {f.familyHeadFirstName} {f.familyHeadLastName}{f.familyName && (' (' + f.familyName + ')')}
                          </strong>
                          <br />
                          📱 {f.familyHeadMobile}
                          {f.familyHeadMobile2 ? ` / ${f.familyHeadMobile2}` : ''}
                          <br /> {f.familyOrigin && ('பூர்வீகம்: ' + f.familyOrigin)}
                        </td>

                        <td>
                          {f.pongalTaxUptoYear || ''}
                        </td>

                        <td>
                          {f.chinnaKovil2025TempleConstructionTotal
                            ? formatINR(f.chinnaKovil2025TempleConstructionTotal)
                            : ''
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {searchResult?.family && (
            <>

              <div className="details-card">
                <div className="detail-item">
                  <span className="detail-label"><strong>குடும்ப எண்:</strong></span> <span className="detail-value">

                    {searchResult.family.id}

                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><strong>கூட்டம்:</strong></span>
                  <span className="detail-value"> {searchResult.family.kootamName}                    </span>
                </div>
                {
                  searchResult.family.customGroup && (
                    <div className="detail-item">
                      <span className="detail-label"><strong>மற்ற குழு:</strong> </span>
                      <span className="detail-value">
                        {searchResult.family.customGroup}
                      </span>
                    </div>
                  )
                }
                <div className="detail-item">
                  <span className="detail-label"><strong>குடும்ப தலைவர்:</strong> </span><span className="detail-value">{searchResult.family.familyHeadFirstName} {searchResult.family.familyHeadLastName}
                  </span>
                </div>
                {
                  searchResult.family.familyName && (
                    <div className="detail-item">
                      <span className="detail-label"><strong>குடும்பப் பெயர்:</strong> </span>
                      <span className="detail-value">
                        {searchResult.family.familyName}
                      </span>
                    </div>
                  )
                }
                {
                  searchResult.family.familyOrigin && (
                    <div className="detail-item">
                      <span className="detail-label"><strong>பூர்வீகம்:</strong> </span>
                      <span className="detail-value">
                        {searchResult.family.familyOrigin}
                      </span>
                    </div>
                  )
                }
                {searchResult.family.tempAddressName_tamil && (
                  <div className="detail-item">
                    <span className="detail-label"><strong>தற்காலிக முகவரி:</strong> </span>
                    <span className="detail-value">
                      {tempAddressTamil && (tempAddressTamil + ', ')}
                      {searchResult.family.tempAddressName_tamil}, {searchResult.family.tempAddressBlock_tamil}, {searchResult.family.tempAddressDistrict_tamil}
                    </span>
                  </div>
                )}

                <div className="detail-item">
                  <span className="detail-label"><strong>நிரந்தர முகவரி:</strong> </span>
                  <span className="detail-value">
                    {permanentAddressTamil && (permanentAddressTamil + ', ')}
                    {searchResult.family.permanentAddressName_tamil}, {searchResult.family.permanentAddressBlock_tamil}, {searchResult.family.permanentAddressDistrict_tamil}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><strong>நிலை:</strong></span>

                  <span style={{ color: ['draft', 'inactive', 'pending'].includes(searchResult.family.status) ? 'red' : 'inherit' }}>
                    {searchResult.family.status || ""}
                    {statusExplanation[searchResult.family.status] || "இது ஒரு அறியப்படாத நிலை."}
                  </span>
                </div>
                {
                  searchResult.family.keyContact && (
                    <div className="detail-item">
                      <span className="detail-label"><strong>முக்கிய தொடர்பு:</strong> </span>
                      <span className="detail-value">
                        {searchResult.family.keyContact ? '✅' : '❌'}
                      </span>
                    </div>
                  )
                }

                {
                  searchResult.family.pongalTaxUptoYear && (
                    <div className="detail-item">
                      <span className="detail-label"><strong>பொங்கல் வரி:</strong> </span>
                      <span className="detail-value">
                        {searchResult.family.pongalTaxUptoYear}
                      </span>
                    </div>
                  )
                }
                {
                  searchResult.family.chinnaKovil2025TempleConstructionTotal && (
                    <div className="detail-item">
                      <span className="detail-label"><strong>திருப்பணி:</strong> </span>
                      <span className="detail-value">
                        {formatINR(searchResult.family.chinnaKovil2025TempleConstructionTotal)}
                      </span>
                    </div>
                  )
                }

                <div className="detail-label">
                  <button
                    type="button"
                    style={{ display: 'inline', marginRight: '4px', marginLeft: '4px' }}  // Adds margin-right between buttons
                    onClick={() => navigate(`/family/view/${searchResult.family.id}`)}
                  >
                    👁️
                  </button>
                  <button
                    type="button"
                    style={{ display: 'inline' }}
                    onClick={() => navigate(`/family/update/${searchResult.family.id}`)}
                  >
                    ✏️
                  </button></div>
              </div>
              {searchResult.family.status !== 'active' && (
                <div style={{ marginTop: '1rem', color: 'red', fontWeight: 'bold' }}>
                  ⚠️ குடும்ப விவரங்கள் முலுமையாக இல்லை. தயவுசெய்து விண்ணப்பப் படிவத்தை பூர்த்தி செய்யவும்.
                  <br></br>
                </div>
              )}
              {searchResult?.members?.length > 0 && (
                <div className="auth_element" >
                  <br />
                  <h3>குடும்ப உறுப்பினர்கள்:</h3>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>பெயர்</th>
                          <th>மொபைல்</th>
                          <th>செயல்கள்</th>
                        </tr>
                      </thead>

                      <tbody>
                        {searchResult.members.map((member, index) => (
                          <tr key={member.uid}>

                            {/* Name */}
                            <td>
                              <strong>{member.firstName} {member.lastName}</strong>
                              <br />
                              <small>{statusExplanation[member.status] || "அறியப்படாத நிலை."}</small>
                            </td>

                            {/* Mobile */}
                            <td>
                              {member.mobile || ''}
                              {member.mobile2 ? (member.mobile ? ' / ' : '') + member.mobile2 : ''}
                            </td>


                            {/* Actions */}
                            <td>
                              <button
                                type="button"
                                style={{ marginRight: '6px' }}
                                onClick={() => navigate(`/family/member-view/${member.id}`)}
                                title="View"
                              >
                                👁️
                              </button>

                              <button
                                type="button"
                                onClick={() => navigate(`/family/member-update/${member.id}`)}
                                title="Edit"
                              >
                                ✏️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}



        </div>
      </div>
    </div>
  );
};

export default FamilySearch;